import React, { useState, useMemo, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResourceRequestStatus } from '~/types';
import { AssignmentDialogColumns } from '~/modules/resourcing/components/ResourceAssignmentDialog/enhancers/constants';
import { useMeContext } from '~/modules/me';

const ResourceAssignmentDialogContext = React.createContext();

export const ResourceAssignmentDialogContextProvider = ({
  children,
  enabledAssignmentDialogColumns,
  resourceRequestId,
  maxSkillLevel,
  resourcePlanGroupBy,
  resourceRequest,
  setEnabledAssignmentDialogColumns,
  assignmentDialogColumnConfiguration,
  isSmartMatchTabSelected,
  setIsSmartMatchTabSelected
}) => {
  const hasPendingAllocation =
    resourceRequest.requestStatus === ResourceRequestStatus.Submitted &&
    resourceRequest.resourceAllocations.length !== 0;
  const [searchCriteria, setSearchCriteria] = useState({
    keywords: [],
    criterions: {}
  });

  const [expandedResourceRequestId, setExpandedResourceRequestId] = useState(
    resourceRequestId
  );

  useEffect(() => setExpandedResourceRequestId(resourceRequestId), [
    setExpandedResourceRequestId,
    resourceRequestId
  ]);

  const [isPendingRequestEditable, setIsPendingRequestEditable] = useState(
    resourceRequest.requestStatus === ResourceRequestStatus.Submitted &&
      !hasPendingAllocation
  );

  const [selectedDialogOptions, setSelectedDialogOptions] = useState([]);

  const [
    availabilityFilterLoadValue,
    setAvailabilityFilterLoadValue
  ] = useState(0);

  const {
    featureFlags: { isRmp2026Q1Enhancements }
  } = useMeContext();

  const [
    isCostOptimizationSmartMatchModeEnabled,
    setIsCostOptimizationSmartMatchModeEnabled
  ] = useState(
    enabledAssignmentDialogColumns.includes(
      AssignmentDialogColumns.SmartMatchIncludingCost
    )
  );

  useEffect(() => {
    setIsPendingRequestEditable(
      resourceRequest.requestStatus === ResourceRequestStatus.Submitted &&
        !hasPendingAllocation
    );
  }, [resourceRequest.requestStatus, hasPendingAllocation]);

  const contextValue = useMemo(
    () => ({
      expandedResourceRequestId,
      setExpandedResourceRequestId,
      availabilityFilterLoadValue,
      setAvailabilityFilterLoadValue,
      resourcePlanGroupBy,
      searchCriteria,
      setSearchCriteria,
      isPendingRequestEditable,
      maxSkillLevel,
      setIsPendingRequestEditable,
      isCostOptimizationSmartMatchModeEnabled,
      setIsCostOptimizationSmartMatchModeEnabled,
      enabledAssignmentDialogColumns,
      setEnabledAssignmentDialogColumns,
      assignmentDialogColumnConfiguration,
      selectedDialogOptions,
      setSelectedDialogOptions,
      isSmartMatchTabSelected,
      setIsSmartMatchTabSelected,
      isRmp2026Q1Enhancements
    }),
    [
      expandedResourceRequestId,
      availabilityFilterLoadValue,
      resourcePlanGroupBy,
      searchCriteria,
      maxSkillLevel,
      isPendingRequestEditable,
      isCostOptimizationSmartMatchModeEnabled,
      enabledAssignmentDialogColumns,
      assignmentDialogColumnConfiguration,
      selectedDialogOptions,
      isSmartMatchTabSelected,
      setExpandedResourceRequestId,
      setAvailabilityFilterLoadValue,
      setSearchCriteria,
      setIsPendingRequestEditable,
      setIsCostOptimizationSmartMatchModeEnabled,
      setEnabledAssignmentDialogColumns,
      setSelectedDialogOptions,
      setIsSmartMatchTabSelected,
      isRmp2026Q1Enhancements
    ]
  );

  return (
    <ResourceAssignmentDialogContext.Provider value={contextValue}>
      {children}
    </ResourceAssignmentDialogContext.Provider>
  );
};

ResourceAssignmentDialogContextProvider.propTypes = {
  resourceRequest: PropTypes.object,
  resourceRequestId: PropTypes.string,
  resourcePlanGroupBy: PropTypes.string,
  children: PropTypes.node,
  maxSkillLevel: PropTypes.number,
  enabledAssignmentDialogColumns: PropTypes.array,
  setEnabledAssignmentDialogColumns: PropTypes.func,
  assignmentDialogColumnConfiguration: PropTypes.object,
  isSmartMatchTabSelected: PropTypes.bool,
  setIsSmartMatchTabSelected: PropTypes.func
};

export const useResourceAssignmentDialogContext = () =>
  useContext(ResourceAssignmentDialogContext);
